const CHANNEL_NAME = "control-hub-tab-session";
const PING_TIMEOUT_MS = 200;

// Marks that *this* tab already validated its session, so a page refresh
// (which closes/reopens the BroadcastChannel) isn't mistaken for a brand
// new browser launch.
const TAB_SESSION_MARKER_KEY = "tabSessionActive";

let channel: BroadcastChannel | null = null;

const getChannel = (): BroadcastChannel | null => {
  if (typeof BroadcastChannel === "undefined") {
    return null;
  }
  if (!channel) {
    channel = new BroadcastChannel(CHANNEL_NAME);
    // Answer other tabs asking whether a live session exists here.
    channel.onmessage = (event: MessageEvent) => {
      if (event.data === "ping") {
        channel?.postMessage("pong");
      }
    };
  }
  return channel;
};

// Starts listening immediately so this tab can answer pings from siblings
// even before it runs its own session check.
getChannel();

export const markTabSessionActive = (): void => {
  sessionStorage.setItem(TAB_SESSION_MARKER_KEY, "1");
};

export const clearTabSessionMarker = (): void => {
  sessionStorage.removeItem(TAB_SESSION_MARKER_KEY);
};

export const isSameTabResume = (): boolean =>
  sessionStorage.getItem(TAB_SESSION_MARKER_KEY) === "1";

// Resolves true if another tab in this browser answers within the timeout.
// If BroadcastChannel isn't supported, fails open (assumes a sibling exists)
// rather than force a logout the user didn't ask for.
export const hasSiblingTab = (): Promise<boolean> =>
  new Promise((resolve) => {
    const ch = getChannel();
    if (!ch) {
      resolve(true);
      return;
    }

    let settled = false;
    const finish = (result: boolean) => {
      if (settled) return;
      settled = true;
      ch.removeEventListener("message", onPong);
      resolve(result);
    };

    const onPong = (event: MessageEvent) => {
      if (event.data === "pong") {
        finish(true);
      }
    };

    ch.addEventListener("message", onPong);
    ch.postMessage("ping");
    setTimeout(() => finish(false), PING_TIMEOUT_MS);
  });
